import { useMemo } from 'react';
import { gql, useFragment } from '@apollo/client';

/**
 * Las carreteras y concesiones de un recorrido, leídas de su ENTIDAD en caché
 * (2026-09-23).
 *
 * El cronómetro sondea `activeSurveillanceRoutes` cada 30 s, y sin cobertura
 * ese sondeo falla: `useLastKnownOnError` le sirve entonces el último resultado
 * bueno, que es una FOTO — no se entera de nada de lo que se escriba después en
 * la caché. Así que añadir la vía de apoyo sin red (el escritor optimista de
 * `submitSurveillanceRouteEdit` o el de `createSegment`) no llegaba al selector
 * de carretera, al PK por GPS ni al detector hasta recuperar la señal. Medido
 * en el E2E `support-road.spec.ts`.
 *
 * La entidad normalizada `SurveillanceRoute:<id>` sí es la verdad al día: con
 * red la reescribe la respuesta del sondeo, y sin ella los escritores
 * optimistas. `undefined` si no está en caché: quien llama cae al dato de la
 * consulta, que es lo de siempre.
 */
const ROUTE_ROADS = gql`
  fragment SurveillanceRouteRoads on SurveillanceRoute {
    id
    roadIDs
    concessionIDs
  }
`;

export type RouteRoads = { roadIDs: string[]; concessionIDs: string[] };

const onlyStrings = (values: ReadonlyArray<unknown> | null | undefined) => {
  const out: string[] = [];
  for (const value of values ?? []) if (typeof value === 'string') out.push(value);
  return out;
};

export const useSurveillanceRouteRoads = (
  routeId: string | null | undefined
): RouteRoads | undefined => {
  const { data } = useFragment<{
    roadIDs?: string[] | null;
    concessionIDs?: string[] | null;
  }>({
    fragment: ROUTE_ROADS,
    from: { __typename: 'SurveillanceRoute', id: routeId ?? '' },
  });
  const known = !!routeId && Array.isArray(data?.roadIDs);
  const roadKey = known ? onlyStrings(data?.roadIDs).join(',') : '';
  const concessionKey = known ? onlyStrings(data?.concessionIDs).join(',') : '';
  // Identidad estable mientras no cambie el CONTENIDO: estas listas son
  // dependencia de los efectos del detector y de los manejadores del
  // cronómetro, y una lista nueva en cada render los relanzaría sin motivo.
  return useMemo(
    () =>
      known
        ? {
            roadIDs: roadKey ? roadKey.split(',') : [],
            concessionIDs: concessionKey ? concessionKey.split(',') : [],
          }
        : undefined,
    [known, roadKey, concessionKey]
  );
};

export default useSurveillanceRouteRoads;
